import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BookOpenCheck,
  CalendarDays,
  ChevronDown,
  CheckCircle2,
  ExternalLink,
  FileText,
  Globe2,
  GraduationCap,
  HandHeart,
  HeartPulse,
  Landmark,
  MapPin,
  Monitor,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import { localizedUrl } from "../lib/fetchLocalized";
import {
  globalPresenceHighlights,
  lineageMoments,
  publicLeadershipMoments,
} from "../data/globalPresence";
import { optimizeCloudinary } from "../utils/cloudinaryImage";

const iconMap = {
  HeartPulse,
  MonitorCog: Monitor,
  Monitor,
  GraduationCap,
  ShieldPlus: ShieldCheck,
  ShieldCheck,
  HandHeart,
  BookOpenCheck,
};

const SectionIntro = ({ eyebrow, title, description, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
      {eyebrow}
    </p>
    <h2 className="text-3xl font-semibold leading-tight text-[#14201f] md:text-5xl">{title}</h2>
    {description && (
      <p className="mt-5 text-base leading-8 text-[#52615f] md:text-lg">{description}</p>
    )}
  </div>
);

const StatCard = ({ value, label, detail }) => (
  <div className="rounded-lg border border-[#d9e5e2] bg-white p-5 shadow-sm">
    <p className="text-3xl font-semibold text-[#9d1f1c] md:text-4xl">{value}</p>
    <p className="mt-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#14201f]">{label}</p>
    <p className="mt-3 text-sm leading-6 text-[#62706e]">{detail}</p>
  </div>
);

const ProgramCard = ({ program, t }) => {
  const Icon = iconMap[program.icon] || Landmark;

  return (
    <article className="group overflow-hidden rounded-lg border border-[#dfe8e6] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="aspect-[4/3] overflow-hidden bg-[#edf4f2]">
        <picture>
          {program.imageMobile ? (
            <source srcSet={program.imageMobile} media="(max-width: 767px)" />
          ) : null}
          <img
            src={program.image}
            alt={program.title}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </picture>
      </div>
      <div className="p-6">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9d1f1c]">
              {program.eyebrow}
            </p>
            <h3 className="mt-2 text-2xl font-semibold leading-tight text-[#14201f]">
              {program.title}
            </h3>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#f8efe5] text-[#9d1f1c]">
            <Icon size={22} />
          </span>
        </div>
        <p className="text-sm leading-7 text-[#52615f]">{program.description}</p>
        <ul className="mt-6 space-y-3">
          {program.metrics?.map((metric) => (
            <li key={metric} className="flex gap-3 text-sm leading-6 text-[#33413f]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
              <span>{metric}</span>
            </li>
          ))}
        </ul>
        <Link
          to={`/programs/${program.id}`}
          className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#9d1f1c] transition hover:text-[#661411]"
        >
          {t("home.programs.exploreProgram")}
          <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();
  const [events, setEvents] = useState([]);
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [activeLeadershipMoment, setActiveLeadershipMoment] = useState(0);
  const heroSlides = useMemo(() => {
    const configuredSlides = Array.isArray(region.heroSlides)
      ? region.heroSlides.filter((slide) => slide?.image)
      : [];
    return configuredSlides.length
      ? configuredSlides
      : [
          {
            image: region.bannerImage,
            imageMobile: region.bannerImageMobile,
            imageAlt: `${siteConfig.brand.name} ${region.label} banner`,
            imagePosition: region.bannerPosition || "center",
            eyebrow: region.eyebrow,
            title: region.title,
            subtitle: region.subtitle,
            primaryCta: region.primaryCta,
            primaryHref: region.donationPath,
            secondaryCta: region.secondaryCta,
            secondaryHref: "/impact",
          },
        ];
  }, [region, siteConfig.brand.name]);
  const activeSlide = heroSlides[activeHeroSlide % heroSlides.length] || heroSlides[0];
  // Unset/legacy slides default to the system default layout ('wideBanner') so every
  // slide renders at the SAME height (no carousel height jump between slide 1 and the rest).
  const activeLayout = activeSlide.imageLayout || "wideBanner";
  const isWideHero = activeLayout === "wideBanner";
  const globalPresence = siteConfig.homePresence?.global || {};
  const publicTrust = siteConfig.homePresence?.publicTrust || {};
  const presenceHighlights = globalPresence.highlights?.length
    ? globalPresence.highlights
    : globalPresenceHighlights;
  const presenceLineage = globalPresence.lineage?.length ? globalPresence.lineage : lineageMoments;
  const leadershipMoments = publicTrust.moments?.length
    ? publicTrust.moments
    : publicLeadershipMoments;
  const featuredLeadershipMoment =
    leadershipMoments[activeLeadershipMoment] || leadershipMoments[0];

  // Pick the hero image source in JS (single <img>, not <picture>): mobile Safari does
  // not reliably re-fetch a <picture> when its <source srcSet> changes after render,
  // which left the mobile banner stuck on a stale/low-res image. A plain <img src>
  // re-fetches on every browser when src + key change. 639px == the sm: layout breakpoint.
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (event) => setIsPhone(event.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    setActiveHeroSlide(0);
  }, [region.key]);

  useEffect(() => {
    if (heroSlides.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveHeroSlide((current) => (current + 1) % heroSlides.length);
    }, 6200);

    return () => window.clearInterval(timer);
  }, [heroSlides.length]);

  useEffect(() => {
    let cancelled = false;

    async function fetchEvents() {
      if (!import.meta.env.VITE_APP_EVENT_API) return;

      try {
        const response = await axios.get(localizedUrl(import.meta.env.VITE_APP_EVENT_API));
        const upcomingEvents = (response.data?.data || [])
          .map((event) => ({
            id: event._id,
            title: event.eventName,
            date: event.eventDate,
            location: event.eventLocation,
            image: event.eventImage,
          }))
          .filter((event) => event.date)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);

        if (!cancelled) setEvents(upcomingEvents);
      } catch (error) {
        if (!cancelled) setEvents([]);
      }
    }

    fetchEvents();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="overflow-x-clip bg-[#f7faf9] text-[#14201f]">
      <section
        id="homeTop"
        className={`relative flex flex-col overflow-hidden bg-[#2a120d] pt-20 text-white sm:block sm:pt-24 lg:pt-28 ${
          isWideHero ? "sm:min-h-[600px] lg:min-h-[800px]" : "min-h-[88vh]"
        }`}
      >
        <div className="relative aspect-[4/5] w-full shrink-0 sm:absolute sm:inset-0 sm:aspect-auto sm:h-auto">
          {heroSlides.map((slide, index) => {
            const isActive = index === activeHeroSlide % heroSlides.length;
            const layout = slide.imageLayout || "wideBanner";
            const isRightPortrait = layout === "rightPortrait";
            const isWideBanner = layout === "wideBanner";
            // Phones use the dedicated portrait mobile image when present; everyone else
            // uses the desktop image. Single <img> + key on the chosen URL guarantees the
            // browser re-fetches the correct, full-resolution image (no <picture> quirks).
            const heroSrc = isPhone && slide.imageMobile ? slide.imageMobile : slide.image;
            return (
              <img
                key={`${heroSrc}-${index}`}
                src={optimizeCloudinary(heroSrc, { width: isPhone ? 1440 : 1920 })}
                alt={slide.imageAlt || slide.title || `${siteConfig.brand.name} banner`}
                fetchPriority={isActive ? "high" : "auto"}
                className={`absolute h-full object-cover transition-opacity duration-1000 ease-out max-sm:!object-top ${
                  isRightPortrait
                    ? "inset-y-0 right-0 w-full lg:h-[118%] lg:w-[60%] lg:-translate-y-[7%]"
                    : isWideBanner
                      ? "inset-0 w-full object-cover sm:inset-y-0 sm:right-0 sm:left-auto sm:w-auto sm:max-w-none sm:object-contain"
                      : "inset-0 w-full"
                } ${isActive ? "opacity-80" : "opacity-0"}`}
                style={{
                  objectPosition: slide.imagePosition || region.bannerPosition || "center",
                }}
              />
            );
          })}
        </div>
        <div className="absolute inset-0 hidden bg-gradient-to-r from-[#21110f] via-[#21110f]/78 to-[#21110f]/28 sm:block" />
        {["rightPortrait", "wideBanner"].includes(activeLayout) && (
          <div
            aria-hidden="true"
            className="absolute bottom-0 right-0 hidden h-56 w-96 bg-[radial-gradient(circle_at_bottom_right,rgba(42,18,13,0.96)_0%,rgba(42,18,13,0.58)_30%,transparent_68%)] lg:block"
          />
        )}
        <div
          className={`container relative z-10 mx-auto flex flex-col justify-end px-4 pb-12 pt-8 sm:px-6 sm:pt-0 lg:px-8 ${
            isWideHero
              ? "sm:min-h-[calc(600px-6rem)] lg:min-h-[calc(800px-7rem)]"
              : "min-h-[calc(88vh-7rem)]"
          }`}
        >
          <div className="grid min-w-0 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div className="min-w-0 max-w-full sm:max-w-4xl">
              <div className="mb-6 flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em]">
                <span className="rounded-full border border-white/30 bg-white/10 px-4 py-2 backdrop-blur">
                  {activeSlide.eyebrow || region.eyebrow}
                </span>
                <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-white/85 backdrop-blur">
                  {region.paymentLabel}
                </span>
              </div>
              <h1 className="max-w-5xl break-words text-2xl font-semibold leading-[1.1] md:text-6xl md:leading-[1.04] lg:text-7xl">
                <span className="line-clamp-3 md:hidden">
                  {activeSlide.titleMobile || activeSlide.title || region.title}
                </span>
                <span className="hidden md:block">{activeSlide.title || region.title}</span>
              </h1>
              <p className="mt-4 max-w-3xl break-words text-base leading-7 text-white/86 md:mt-7 md:text-xl md:leading-8">
                <span className="line-clamp-2 md:hidden">
                  {activeSlide.subtitleMobile || activeSlide.subtitle || region.subtitle}
                </span>
                <span className="hidden md:block">{activeSlide.subtitle || region.subtitle}</span>
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <Link
                  to={activeSlide.primaryHref || region.donationPath}
                  className="inline-flex w-full items-center justify-center gap-3 rounded-md bg-[#f5c45e] px-5 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#201711] transition hover:bg-[#ffd77c] sm:w-auto sm:px-6 sm:tracking-[0.16em]"
                >
                  {activeSlide.primaryCta || region.primaryCta}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to={activeSlide.secondaryHref || "/impact"}
                  className="inline-flex w-full items-center justify-center gap-3 rounded-md border border-white/45 px-5 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-white hover:text-[#14201f] sm:w-auto sm:px-6 sm:tracking-[0.16em]"
                >
                  {activeSlide.secondaryCta || region.secondaryCta}
                  <ArrowRight size={18} />
                </Link>
              </div>
              {heroSlides.length > 1 && (
                <div
                  className="mt-8 flex flex-wrap items-center gap-3"
                  aria-label={t("home.hero.bannerSlidesLabel")}
                >
                  {heroSlides.map((slide, index) => {
                    const isActive = index === activeHeroSlide % heroSlides.length;
                    return (
                      <button
                        key={`${slide.title}-${index}`}
                        type="button"
                        onClick={() => setActiveHeroSlide(index)}
                        className={`group flex h-10 w-16 items-center rounded-full border px-2 transition ${
                          isActive
                            ? "border-[#f5c45e] bg-[#f5c45e]/14"
                            : "border-white/24 bg-white/8 hover:border-white/54"
                        }`}
                        aria-label={t("home.hero.showBannerLabel", {
                          index: index + 1,
                          label: slide.eyebrow || slide.title,
                        })}
                        aria-current={isActive ? "true" : "false"}
                      >
                        <span className="h-1.5 w-full overflow-hidden rounded-full bg-white/25">
                          <span
                            className={`block h-full rounded-full bg-[#f5c45e] transition-all duration-500 ${
                              isActive ? "w-full" : "w-0 group-hover:w-1/2"
                            }`}
                          />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {!activeSlide.hideHeroPanel && (
              <div className="hidden min-w-0 max-w-full border-t border-white/25 pt-6 sm:max-w-none lg:block lg:border-l lg:border-t-0 lg:pl-8">
                {activeSlide.featureImage ? (
                  <figure className="mb-6 overflow-hidden rounded-lg border border-white/18 bg-white/10 shadow-2xl shadow-black/25 backdrop-blur">
                    <div
                      className={`overflow-hidden bg-[#2a120d] ${
                        activeSlide.featureAspect === "portrait"
                          ? "aspect-[4/5] max-h-[520px] lg:ml-auto lg:max-w-[420px]"
                          : "aspect-[16/10] lg:aspect-[4/3]"
                      }`}
                    >
                      <img
                        src={activeSlide.featureImage}
                        alt={activeSlide.featureAlt || activeSlide.imageAlt || activeSlide.title}
                        className="h-full w-full object-cover"
                        style={{ objectPosition: activeSlide.featureObjectPosition || "center" }}
                        loading="eager"
                      />
                    </div>
                    {activeSlide.featureCaption && (
                      <figcaption className="border-t border-white/12 p-4 text-sm font-medium leading-6 text-white/82">
                        {activeSlide.featureCaption}
                      </figcaption>
                    )}
                  </figure>
                ) : (
                  <p className="text-sm leading-7 text-white/80">{siteConfig.brand.promise}</p>
                )}
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {siteConfig.impactStats.slice(0, 4).map((stat) => (
                    <div key={stat.label} className="border-t border-white/20 pt-4">
                      <p className="text-2xl font-semibold text-[#f5c45e]">{stat.value}</p>
                      <p className="mt-1 break-words text-xs uppercase tracking-[0.12em] text-white/78 sm:tracking-[0.16em]">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="impact" className="border-b border-[#dce8e5] bg-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {siteConfig.impactStats.map((stat) => (
              <StatCard key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f8efe5] py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-stretch">
            <div className="lg:min-h-full">
              <div className="lg:sticky lg:top-32">
                <SectionIntro
                  eyebrow={t("home.usaPresence.eyebrow")}
                  title={siteConfig.usaPresence.title}
                  description={siteConfig.usaPresence.description}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {siteConfig.usaPresence.events.map((event) => (
                <article
                  key={`${event.year}-${event.title}`}
                  className="overflow-hidden rounded-lg border border-[#e5d1bf] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  {event.imageUrl && (
                    <div className="aspect-[16/10] overflow-hidden bg-[#f1e4d8]">
                      <picture>
                        {event.imageMobile ? (
                          <source srcSet={event.imageMobile} media="(max-width: 767px)" />
                        ) : null}
                        <img
                          src={event.imageUrl}
                          alt={event.imageAlt || event.title}
                          className="h-full w-full object-cover transition duration-700 hover:scale-105"
                          loading="lazy"
                        />
                      </picture>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8f241f]">
                          {event.year}
                        </p>
                        <h3 className="mt-3 text-xl font-semibold leading-tight text-[#14201f]">
                          {event.title}
                        </h3>
                      </div>
                      <span className="rounded-md bg-[#f5e1ca] px-3 py-2 text-xs font-semibold text-[#6f231d]">
                        {event.place}
                      </span>
                    </div>
                    <p className="mt-4 text-sm leading-7 text-[#52615f]">{event.text}</p>
                    <a
                      href={event.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#9d1f1c] transition hover:text-[#661411]"
                    >
                      {t("home.usaPresence.sourcePrefix")} {event.sourceLabel}
                      <ExternalLink size={15} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.86fr_1.14fr] lg:items-center lg:px-8">
          <div className="overflow-hidden rounded-lg border border-[#eaded2] bg-[#f7faf9] shadow-sm">
            <img
              src={siteConfig.swamiji.image}
              alt={siteConfig.swamiji.title}
              className="h-full min-h-[420px] w-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <div>
            <SectionIntro
              eyebrow={siteConfig.swamiji.eyebrow}
              title={siteConfig.swamiji.title}
              description={siteConfig.swamiji.description}
            />
            <ul className="mt-8 grid gap-4">
              {siteConfig.swamiji.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-3 text-sm leading-7 text-[#33413f]">
                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {siteConfig.swamiji.audiencePaths?.map((path) => (
                <article
                  key={path.title}
                  className="rounded-lg border border-[#eaded2] bg-[#f8efe5] p-5"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9d1f1c]">
                    {path.eyebrow}
                  </p>
                  <h3 className="mt-3 text-xl font-semibold leading-tight text-[#14201f]">
                    {path.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-[#52615f]">{path.text}</p>
                  {path.ctaHref?.startsWith("http") ? (
                    <a
                      href={path.ctaHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#9d1f1c] transition hover:text-[#661411]"
                    >
                      {path.ctaLabel}
                      <ExternalLink size={15} />
                    </a>
                  ) : (
                    <Link
                      to={path.ctaHref || region.donationPath}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#9d1f1c] transition hover:text-[#661411]"
                    >
                      {path.ctaLabel}
                      <ArrowRight size={15} />
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fffaf5] py-20 md:py-28">
        <div className="container mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.42fr_0.58fr] lg:items-stretch lg:px-8">
          <div className="lg:min-h-full">
            <div className="lg:sticky lg:top-32">
              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-md bg-[#f8efe5] text-[#9d1f1c]">
                <Sparkles size={24} />
              </div>
              <SectionIntro
                eyebrow={globalPresence.eyebrow || t("home.globalPresence.eyebrow")}
                title={globalPresence.title || t("home.globalPresence.title")}
                description={globalPresence.description || t("home.globalPresence.description")}
              />
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={globalPresence.primaryCtaHref || "/global-presence"}
                  className="inline-flex items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
                >
                  {globalPresence.primaryCtaLabel || t("home.globalPresence.primaryCta")}
                  <ArrowRight size={17} />
                </Link>
                <Link
                  to={globalPresence.secondaryCtaHref || "/vedant"}
                  className="inline-flex items-center justify-center gap-3 rounded-md border border-[#d8c7ba] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#4b2418] transition hover:border-[#9d1f1c] hover:text-[#9d1f1c]"
                >
                  {globalPresence.secondaryCtaLabel || t("home.globalPresence.secondaryCta")}
                  <ArrowRight size={17} />
                </Link>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {[...presenceHighlights.slice(0, 2), ...presenceLineage.slice(0, 2)].map(
              (item, index) => (
                <article
                  key={item.title}
                  className={`group overflow-hidden rounded-lg border border-[#ead9cd] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
                    index % 2 ? "md:translate-y-8" : ""
                  }`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[#f8efe5]">
                    <picture>
                      {item.imageMobile ? (
                        <source srcSet={item.imageMobile} media="(max-width: 767px)" />
                      ) : null}
                      <img
                        src={item.image}
                        alt={item.alt}
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        style={{ objectPosition: item.objectPosition || "center" }}
                        loading="lazy"
                      />
                    </picture>
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
                      {item.eyebrow}
                    </p>
                    <h3 className="mt-3 text-xl font-semibold leading-tight text-[#241511]">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-[#5f4a3e]">{item.text}</p>
                  </div>
                </article>
              )
            )}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.52fr_0.48fr] lg:items-start lg:px-8">
          <div className="lg:sticky lg:top-28">
            <div className="overflow-hidden rounded-lg border border-[#ead9cd] bg-[#fffaf5] shadow-sm">
              <div
                className="overflow-hidden bg-[#f8efe5]"
                style={{ aspectRatio: featuredLeadershipMoment.featureAspect || "16 / 11" }}
              >
                <picture>
                  {featuredLeadershipMoment.featureImageMobile ||
                  featuredLeadershipMoment.imageMobile ? (
                    <source
                      srcSet={
                        featuredLeadershipMoment.featureImageMobile ||
                        featuredLeadershipMoment.imageMobile
                      }
                      media="(max-width: 767px)"
                    />
                  ) : null}
                  <img
                    src={featuredLeadershipMoment.featureImage || featuredLeadershipMoment.image}
                    alt={featuredLeadershipMoment.featureAlt || featuredLeadershipMoment.alt}
                    className={`h-full w-full transition duration-500 ${
                      featuredLeadershipMoment.featureImageFit === "cover"
                        ? "object-cover"
                        : "object-contain"
                    }`}
                    style={{
                      objectPosition:
                        featuredLeadershipMoment.featureObjectPosition ||
                        featuredLeadershipMoment.objectPosition ||
                        "center",
                    }}
                    loading="lazy"
                  />
                </picture>
              </div>
              <div className="border-t border-[#ead9cd] p-5">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#9d1f1c]">
                  {featuredLeadershipMoment.eyebrow}
                </p>
                <p className="mt-2 text-lg font-semibold leading-tight text-[#241511]">
                  {featuredLeadershipMoment.title}
                </p>
              </div>
            </div>
          </div>

          <div>
            <SectionIntro
              eyebrow={publicTrust.eyebrow || t("home.publicTrust.eyebrow")}
              title={publicTrust.title || t("home.publicTrust.title")}
              description={publicTrust.description || t("home.publicTrust.description")}
            />
            <div className="mt-8 divide-y divide-[#ead9cd] overflow-hidden rounded-lg border border-[#ead9cd] bg-[#fffaf5]">
              {leadershipMoments.slice(0, 4).map((moment, index) => {
                const isOpen = index === activeLeadershipMoment;
                return (
                  <article key={moment.title}>
                    <button
                      type="button"
                      onClick={() => setActiveLeadershipMoment(index)}
                      className="flex w-full items-start justify-between gap-4 px-5 py-5 text-left transition hover:bg-white"
                      aria-expanded={isOpen}
                    >
                      <span>
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
                          {moment.category}
                        </span>
                        <span className="mt-2 block text-lg font-semibold leading-tight text-[#241511]">
                          {moment.title}
                        </span>
                      </span>
                      <ChevronDown
                        className={`mt-1 h-5 w-5 shrink-0 text-[#9d1f1c] transition ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div
                      className={`grid transition-all duration-300 ${
                        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-5 pb-5">
                          <p className="text-sm leading-7 text-[#5f4a3e]">{moment.text}</p>
                          <Link
                            to={publicTrust.galleryCtaHref || "/global-presence"}
                            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#9d1f1c] transition hover:text-[#661411]"
                          >
                            {t("home.publicTrust.galleryCta")}
                            <ArrowRight size={15} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="programs" className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <SectionIntro
              eyebrow={t("home.programs.eyebrow")}
              title={t("home.programs.title")}
              description={t("home.programs.description")}
            />
            <Link
              to={region.donationPath}
              className="inline-flex w-fit items-center gap-3 rounded-md bg-[#4b2418] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#7f1916]"
            >
              {region.paymentLabel}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {siteConfig.programPillars.map((program) => (
              <ProgramCard key={program.id} program={program} t={t} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow={t("home.stories.eyebrow")}
            title={t("home.stories.title")}
            description={t("home.stories.description")}
            align="center"
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {siteConfig.stories.map((story) => (
              <article
                key={story.title}
                className="overflow-hidden rounded-lg border border-[#dfe8e6] bg-[#f7faf9]"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-7">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#9d1f1c]">
                    {story.eyebrow}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold text-[#14201f]">{story.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-[#52615f]">{story.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="vedant"
        className="relative overflow-hidden bg-[#4b2418] py-20 text-white md:py-28"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,rgba(157,31,28,0.52),transparent_28%),radial-gradient(circle_at_86%_12%,rgba(245,196,94,0.14),transparent_28%),linear-gradient(135deg,#2a120d_0%,#4b2418_48%,#7f1916_100%)]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
        <div className="container relative z-10 mx-auto grid gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-8">
          <div className="relative overflow-hidden rounded-lg border border-[#f5c45e]/28 bg-[#2a120d]/58 p-2 shadow-2xl shadow-black/30">
            <div className="overflow-hidden rounded-md">
              <img
                src={siteConfig.vedant.image}
                alt={t("home.vedant.imageAlt")}
                className="aspect-[16/11] w-full object-cover object-center lg:aspect-[5/4]"
                loading="lazy"
              />
            </div>
            <div className="absolute inset-x-2 bottom-2 rounded-b-md bg-gradient-to-t from-[#2a120d]/92 via-[#2a120d]/42 to-transparent p-5">
              <p className="max-w-md text-xs font-semibold uppercase tracking-[0.22em] text-[#f5c45e]">
                {t("home.vedant.imageCaption")}
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#f5c45e]">
              {t("home.vedant.eyebrow")}
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-[#fffaf2] md:text-5xl">
              {siteConfig.vedant.title}
            </h2>
            <p className="mt-6 text-lg leading-9 text-[#f8eadc]/84">{siteConfig.vedant.quote}</p>
            <div className="mt-9 grid gap-4 sm:grid-cols-2">
              {siteConfig.vedant.principles.map((principle) => (
                <div key={principle} className="border-t border-[#f5c45e]/22 pt-4">
                  <CheckCircle2 className="mb-3 h-5 w-5 text-[#f5c45e]" />
                  <p className="text-sm leading-7 text-[#f8eadc]/86">{principle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="transparency" className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <SectionIntro
              eyebrow={t("home.transparency.eyebrow")}
              title={siteConfig.transparency.title}
              description={siteConfig.transparency.description}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm">
                <ReceiptText className="h-7 w-7 text-[#9d1f1c]" />
                <h3 className="mt-5 text-xl font-semibold text-[#14201f]">
                  {t("home.transparency.clearUsGiving.title")}
                </h3>
                <p className="mt-3 text-sm leading-7 text-[#52615f]">
                  {t("home.transparency.clearUsGiving.description")}
                </p>
              </div>
              <div className="rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm">
                <Globe2 className="h-7 w-7 text-[#9d1f1c]" />
                <h3 className="mt-5 text-xl font-semibold text-[#14201f]">
                  {t("home.transparency.programAccountability.title")}
                </h3>
                <p className="mt-3 text-sm leading-7 text-[#52615f]">
                  {t("home.transparency.programAccountability.description")}
                </p>
              </div>
              {siteConfig.transparency.reports.map((report) => (
                <a
                  key={report.label}
                  href={report.href}
                  className="rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <FileText className="h-7 w-7 text-[#9d1f1c]" />
                  <h3 className="mt-5 text-xl font-semibold text-[#14201f]">{report.label}</h3>
                  <p className="mt-2 text-sm text-[#52615f]">{report.note}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow={t("home.gallery.eyebrow")}
            title={t("home.gallery.title")}
            description={t("home.gallery.description")}
            align="center"
          />
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {siteConfig.gallery.map((image) => (
              <figure
                key={image.src}
                className="group overflow-hidden rounded-lg border border-[#dfe8e6] bg-white"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4 text-sm font-semibold text-[#14201f]">
                  {image.label}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f8efe5] py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow={t("home.joinSeva.eyebrow")}
            title={t("home.joinSeva.title")}
            description={t("home.joinSeva.description")}
            align="center"
          />
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {siteConfig.homepageAdditions.map((item) => (
              <article
                key={item.title}
                className="rounded-lg border border-[#e5d1bf] bg-white p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold text-[#14201f]">{item.title}</h3>
                <p className="mt-4 text-sm leading-7 text-[#52615f]">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <SectionIntro
              eyebrow={t("home.engage.eyebrow")}
              title={t("home.engage.title")}
              description={region.contactLine}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Link
                to={region.donationPath}
                className="rounded-lg bg-[#9d1f1c] p-6 text-white shadow-lg transition hover:-translate-y-1 hover:bg-[#7f1916]"
              >
                <HandHeart className="h-7 w-7" />
                <h3 className="mt-5 text-2xl font-semibold">{region.paymentLabel}</h3>
                <p className="mt-3 text-sm leading-7 text-white/82">
                  {t("home.engage.donateDescription")}
                </p>
              </Link>
              <Link
                to="/volunteer"
                className="rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
              >
                <Users className="h-7 w-7 text-[#9d1f1c]" />
                <h3 className="mt-5 text-2xl font-semibold text-[#14201f]">
                  {t("home.engage.volunteerTitle")}
                </h3>
                <p className="mt-3 text-sm leading-7 text-[#52615f]">
                  {t("home.engage.volunteerDescription")}
                </p>
              </Link>
            </div>
          </div>

          {events.length > 0 && (
            <div className="mt-16 rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9d1f1c]">
                    {t("home.events.eyebrow")}
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold text-[#14201f]">
                    {t("home.events.title")}
                  </h3>
                </div>
                <CalendarDays className="h-7 w-7 text-[#9d1f1c]" />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {events.map((event) => (
                  <div key={event.id} className="border-t border-[#dfe8e6] pt-4">
                    <p className="text-sm font-semibold text-[#14201f]">{event.title}</p>
                    <p className="mt-2 flex items-center gap-2 text-sm text-[#52615f]">
                      <CalendarDays size={15} />
                      {new Date(event.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                    {event.location && (
                      <p className="mt-2 flex items-center gap-2 text-sm text-[#52615f]">
                        <MapPin size={15} />
                        {event.location}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Home;
